import {
	Component, Input, Output, EventEmitter, SimpleChanges, OnDestroy,
	OnChanges
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ProtocolSettings, ProtocolSettingsTypes, PrtgSettings } from 'DiscoveryEngine.TS/Protocols';
import { Guid } from "DiscoveryEngine.TS/Utils";
import { ComponentBase, Changeable } from "common";
import { NavigationService } from 'netview';
import { DiscResultsService } from 'discovery';
import {
	DisplayDeviceGroupTreeDto, DisplayDeviceGroupContainerDto,
	DisplayDeviceGroupDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { SchedDiscPrtgExport } from 'DiscoveryEngine.TS/PRTG';
import { IdentityService, LicensingService } from 'identity';
import { MessageService } from 'base';
import { SelectItem } from 'primeng/api';
import { ToolsService } from 'tools';
import { PrtgConnectResultDto } from '../../../UvExplorerServer.Dtos.TS/Tools';


@Component({
	selector: 'prtg-export-config',
	templateUrl: '../prtg-export-config/prtg-export-config.component.html',
	styleUrls: ['../prtg-export-config/prtg-export-config.component.scss']
})
export class PrtgExportConfigComponent extends ComponentBase implements OnDestroy, OnChanges, Changeable {

	@Input()
	public Network: NetViewDto;

	@Input()
	public PrtgExport: SchedDiscPrtgExport;

	@Input()
	public EnableExportOrigin: boolean;

	public SettingsForm: UntypedFormGroup;

	public EnableExport: UntypedFormControl;
	public ServerSettings: UntypedFormControl;
	public ServerSettings2: UntypedFormControl;
	public ServerSettings3: UntypedFormControl;
	public ServerSettings4: UntypedFormControl;
	public ExportOrigin: UntypedFormControl;
	public ExportDataOnly: UntypedFormControl;
	public ParentGroupName: UntypedFormControl;
	public GroupName: UntypedFormControl;
	public RemoveMissingDevices: UntypedFormControl;
	public ExportMapsOnly: UntypedFormControl;
	public MergeMapsOnExport: UntypedFormControl;
	public SensorTypes: UntypedFormControl;
	public UseTemplates: UntypedFormControl;
	public ExportMaps: UntypedFormControl;

	private _PrtgSettings: PrtgSettings[];
	private _AllMaps: Object[];
	private _MapData: Object[];

	// PRTG group picker state. Two visibility flags so the Parent Group and
	// Group Name "Browse..." buttons can each open the picker independently
	// and their on-select handlers stay separate.
	public parentPickerVisible: boolean = false;
	public groupPickerVisible: boolean = false;

	private deviceGroupTreeLoadedSub: Subscription;
	private deviceGroupTreeChangedSub: Subscription;

	public SensorOptions: SelectItem[];

	constructor(private fb: UntypedFormBuilder,
		private navService: NavigationService,
		private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private messageService: MessageService,
		private toolsService: ToolsService) {

		super();

		this.Network = null;
		this.PrtgExport = null;
		this.EnableExportOrigin = false;

		this.deviceGroupTreeLoadedSub = this.discResultsService.onDeviceGroupTreeLoaded.subscribe(
			(result: DisplayDeviceGroupTreeDto) => {
				this.resetMapData();
			},
			(error?) => {
				this.resetMapData();
			});

		this.deviceGroupTreeChangedSub = this.discResultsService.onDeviceGroupTreeChanged.subscribe(
			(result: boolean) => {
				this.resetMapData();
			},
			(error?) => {
				this.resetMapData();
			});

		this.SensorOptions = [
			{ label: "Ping", value: "ping" },
			{ label: "SNMP CPU", value: "snmp-cpu" },
			{ label: "SNMP Memory", value: "snmp-memory" },
			{ label: "SNMP Disk Free", value: "snmp-disk-free" },
			{ label: "SNMP Up-Time", value: "snmp-up-time" },
			{ label: "SNMP Traffic/IF", value: "snmp-if" },
			{ label: "SNMP Traffic/IF (All UP IFs)", value: "snmp-if-up" },
			{ label: "SNMP Traffic/IF (All PHYSICAL IFs)", value: "snmp-if-physical" },
			{ label: "SNMP Traffic/IF (All UPLINK IFs)", value: "snmp-if-uplink" },
			{ label: "WMI CPU", value: "wmi-cpu" },
			{ label: "WMI Memory", value: "wmi-memory" },
			{ label: "WMI Disk Space", value: "wmi-disk-space" },
			{ label: "WMI Up-Time", value: "wmi-up-time" },
		];

		this.BuildForm();
	}

	public ngOnDestroy(): void {
		if (this.deviceGroupTreeLoadedSub) {
			this.deviceGroupTreeLoadedSub.unsubscribe();
			this.deviceGroupTreeLoadedSub = undefined;
		}
		if (this.deviceGroupTreeChangedSub) {
			this.deviceGroupTreeChangedSub.unsubscribe();
			this.deviceGroupTreeChangedSub = undefined;
		}
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	private resetMapData(): void {
		this._AllMaps = undefined;
		this._MapData = undefined;
	}

	protected BuildForm(): void {
		let enableExport = false;
		let serverSettings = "";
		let serverSettings2 = "";
		let serverSettings3 = "";
		let serverSettings4 = "";
		let exportOrigin = "";
		let exportDataOnly = '1';
		let parentGroupName = "";
		let groupName = "";
		let removeMissingDevices = false;
		let exportMapsOnly = false;
		let mergeMapsOnExport = false;
		let sensorTypes: string[] = [];
		let useTemplates = false;
		let exportMaps = false;

		if (this.PrtgExport) {
			let value = this.PrtgExport;

			enableExport = value.EnableExport;
			if (value.HasServerSettings)
				serverSettings = value.ServerSettings.GuidStr;
			if (value.HasServerSettings2)
				serverSettings2 = value.ServerSettings2.GuidStr;
			if (value.HasServerSettings3)
				serverSettings3 = value.ServerSettings3.GuidStr;
			if (value.HasServerSettings4)
				serverSettings4 = value.ServerSettings4.GuidStr;
			if (value.HasExportOrigin)
				exportOrigin = value.ExportOrigin;
			exportDataOnly = (value.ExportMasterNetworkOnly ? '1' : '2');
			if (value.HasParentGroupName)
				parentGroupName = value.ParentGroupName;
			if (value.HasGroupName)
				groupName = value.GroupName;
			removeMissingDevices = value.RemoveMissingDevices;
			exportMapsOnly = value.ExportMapsOnly;
			mergeMapsOnExport = value.MergeMapsOnExport;
			useTemplates = value.UseTemplates;	
			exportMaps = value.ExportMaps;

			if (value.AddPingMonitors) {
				sensorTypes.push("ping");
			}
			if (value.AddSnmpCpuMonitors) {
				sensorTypes.push("snmp-cpu");
			}
			if (value.AddSnmpIfMonitors) {
				sensorTypes.push("snmp-if");
			}
			if (value.AddSnmpIfMonitors_AllUp) {
				sensorTypes.push("snmp-if-up");
			}
			if (value.AddSnmpIfMonitors_AllPhysical) {
				sensorTypes.push("snmp-if-physical");
			}
			if (value.AddSnmpIfMonitors_AllUplinks) {
				sensorTypes.push("snmp-if-uplink");
			}
			if (value.AddSnmpMemoryMonitors) {
				sensorTypes.push("snmp-memory");
			}
			if (value.AddSnmpDiskFreeMonitors) {
				sensorTypes.push("snmp-disk-free");
			}
			if (value.AddSnmpUpTimeMonitors) {
				sensorTypes.push("snmp-up-time");
			}
			if (value.AddWmiCpuMonitors) {
				sensorTypes.push("wmi-cpu");
			}
			if (value.AddWmiDiskSpaceMonitors) {
				sensorTypes.push("wmi-disk-space");
			}
			if (value.AddWmiMemoryMonitors) {
				sensorTypes.push("wmi-memory");
			}
			if (value.AddWmiUpTimeMonitors) {
				sensorTypes.push("wmi-up-time");
			}
		}

		this.EnableExport = new UntypedFormControl(enableExport);
		this.ServerSettings = new UntypedFormControl(serverSettings);
		this.ServerSettings2 = new UntypedFormControl(serverSettings2);
		this.ServerSettings3 = new UntypedFormControl(serverSettings3);
		this.ServerSettings4 = new UntypedFormControl(serverSettings4);
		this.ExportOrigin = new UntypedFormControl(exportOrigin);
		this.ExportDataOnly = new UntypedFormControl(exportDataOnly);
		this.ParentGroupName = new UntypedFormControl(parentGroupName);
		this.GroupName = new UntypedFormControl(groupName);
		this.RemoveMissingDevices = new UntypedFormControl(removeMissingDevices);
		this.ExportMapsOnly = new UntypedFormControl(exportMapsOnly);
		this.MergeMapsOnExport = new UntypedFormControl(mergeMapsOnExport);
		this.SensorTypes = new UntypedFormControl(sensorTypes);
		this.UseTemplates = new UntypedFormControl(useTemplates);
		this.ExportMaps = new UntypedFormControl(exportMaps);

		this.SettingsForm = this.fb.group({
			EnableExport: this.EnableExport,
			ServerSettings: this.ServerSettings,
			ServerSettings2: this.ServerSettings2,
			ServerSettings3: this.ServerSettings3,
			ServerSettings4: this.ServerSettings4,
			ExportOrigin: this.ExportOrigin,
			ExportDataOnly: this.ExportDataOnly,
			ParentGroupName: this.ParentGroupName,
			GroupName: this.GroupName,
			RemoveMissingDevices: this.RemoveMissingDevices,
			ExportMapsOnly: this.ExportMapsOnly,
			MergeMapsOnExport: this.MergeMapsOnExport,
			SensorTypes: this.SensorTypes,
			UseTemplates: this.UseTemplates,
			ExportMaps: this.ExportMaps,
		},
		{
			validator: Validators.compose([
				this.validPrtgSettings(this.EnableExport, this.ServerSettings),

				// Group Name is now optional
				//this.validGroupName(this.EnableExport, this.GroupName)
			])
		});
	}

	public get ExportEnabled(): boolean {
		if (this.EnableExport) {
			return this.EnableExport.value;
		}
		else {
			return false;
		}
	}

	public get ExportDevices(): boolean {
		if (this.ExportDataOnly) {
			return (this.ExportEnabled && this.ExportDataOnly.value == '2');
		}
		else {
			return false;
		}
	}

	public get EnableMapPicker(): boolean {
		if (!!this.ExportMaps) {
			return (this.ExportDevices && this.ExportMaps.value);
		}
		else {
			return false;
		}
	}

	public get ServerSettingsSummary(): string {
		if (this.ServerSettings.value) {
			let settings = this.FindSettingsByGuid(this.ServerSettings.value);
			if (settings)
				return settings.Summary;
		}
		return "";
	}

	private FindSettingsByGuid(guid: string): PrtgSettings {
		guid = guid.toLowerCase();
		for (let i = 0; i < this._PrtgSettings.length; ++i) {
			let settings = this._PrtgSettings[i];
			if (settings.GUID.toLowerCase() == guid)
				return settings;
		}
		return null;
	}

	public get PrtgSettings(): PrtgSettings[] {
		if (!this._PrtgSettings && this.Network) {
			this._PrtgSettings = [];
			let emptyPS = new PrtgSettings();
			emptyPS.Name = "";
			emptyPS.GUID = "";
			this._PrtgSettings.push(emptyPS);

			this.Network.ProtocolSettings.ForEach((ps: ProtocolSettings) => {
				if (ps.SettingsType == ProtocolSettingsTypes.Prtg) {
					this._PrtgSettings.push(<PrtgSettings>ps);
				}
			}, this);
		}
		return this._PrtgSettings;
	}

	private get AllMaps(): Object[] {
		if (!this._AllMaps) {
			this._AllMaps = [];

			if (this.discResultsService.deviceGroupTree) {
				let tree = this.discResultsService.deviceGroupTree;
				let userGroupsContainer = tree.UserGroupsContainer;
				let subnetGroupsContainer = tree.SubnetGroupsContainer;
				let vlanGroupsContainer = tree.VlanGroupsContainer;

				// List user groups first
				if (userGroupsContainer) {
					userGroupsContainer.Groups.ForEach((ddg: DisplayDeviceGroupDto) => {
						this._AllMaps.push(this.MakeMapEntry(userGroupsContainer, ddg));
					}, this);
				}

				// List system groups second (except Subnets and Vlans)
				tree.RootContainer.Containers.ForEach((ddgc: DisplayDeviceGroupContainerDto) => {
					if (ddgc != userGroupsContainer &&
						ddgc != subnetGroupsContainer &&
						ddgc != vlanGroupsContainer) {

						ddgc.Groups.ForEach((ddg: DisplayDeviceGroupDto) => {
							this._AllMaps.push(this.MakeMapEntry(ddgc, ddg));
						}, this);
					}
				}, this);
			}
		}
		return this._AllMaps;
	}

	private MakeMapEntry(ddgc: DisplayDeviceGroupContainerDto, ddg: DisplayDeviceGroupDto): Object {
		return {
			Owner: ddg.Owner,
			Guid: new Guid(ddg.VdgTagGuid),
			Name: ddg.Name,
			Description: ddgc.Name,
			//PrtgId: 1000 + i
		};
	}

	public get MapData(): Object[] {
		if (!this._MapData && this.PrtgExport) {
			this._MapData = [];
			this.AllMaps.forEach((map: any) => {
				let isSelected = this.PrtgExport.DeviceCategories.Contains(map.Guid) ||
					this.PrtgExport.DeviceGroups.Contains(map.Guid);

				let mapData = {
					Selected: isSelected,
					Owner: map.Owner,
					Guid: map.Guid,
					Name: map.Name,
					Description: map.Description
					//, PrtgId: map.PrtgId,
				}

				this._MapData.push(mapData);
			}, this);
		}
		return this._MapData;
	}

	public get SelectAllState(): any {
		let selectionState = false;
		if (this._MapData) {
			let selected = 0;
			let unselected = 0;
			this._MapData.forEach((data: any) => {
				if (data.Selected)
					++selected;
				else
					++unselected;
			});
			if (selected == 0 && unselected == 0)
				selectionState = false;
			else if (selected == 0)
				selectionState = false;
			else if (unselected == 0)
				selectionState = true;
			else
				selectionState = false;
		}
		return selectionState;
	}

	public SelectAllChanged(event: any): void {
		if (this.SelectAllState)
			this.DeselectAllMaps();
		else
			this.SelectAllMaps();

		this.SettingsForm.markAsDirty();
	}

	private SelectAllMaps(): void {
		if (this._MapData) {
			this._MapData.forEach((data: any) => data.Selected = true);
		}
	}

	private DeselectAllMaps(): void {
		if (this._MapData) {
			this._MapData.forEach((data: any) => data.Selected = false);
		}
	}

	public MapSelectionChanged(event: any, data: any): void {
		data.Selected = !data.Selected;

		this.SettingsForm.markAsDirty();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this._PrtgSettings = undefined;
		this.RebuildMapData();
		this.BuildForm();
	}

	private RebuildMapData(): void {
		this._MapData = null;
		let tmp = this.MapData;
	}

	public get tempChanges(): boolean {
		return this.SettingsForm.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.SettingsForm.valid;
	}

	public discardTempChanges(): void {
		this.BuildForm();
	}

	private contains(arr: string[], str: string): boolean {
		return (arr.indexOf(str) >= 0);
	}

	public applyTempChanges(): void {
		if (this.PrtgExport) {
			let value = this.PrtgExport;

			value.EnableExport = this.EnableExport.value;
			value.ServerSettings = (this.ServerSettings.value ? new Guid(this.ServerSettings.value) : null);
			value.ServerSettings2 = (this.ServerSettings2.value ? new Guid(this.ServerSettings2.value) : null);
			value.ServerSettings3 = null;
			if (this.ServerSettings3.value) {
				if (this.ServerSettings.value == "")
				value.ServerSettings3 = (this.ServerSettings3.value ? new Guid(this.ServerSettings3.value) : null);
			}
			value.ServerSettings4 = null;
			if (this.ServerSettings4.value) {
				value.ServerSettings4 = (this.ServerSettings4.value ? new Guid(this.ServerSettings4.value) : null);
			}
			value.ExportOrigin = this.ExportOrigin.value;
			value.ParentGroupName = this.ParentGroupName.value;
			value.GroupName = this.GroupName.value;
			value.RemoveMissingDevices = this.RemoveMissingDevices.value;
			value.ExportMapsOnly = this.ExportMapsOnly.value;
			value.ExportMasterNetworkOnly = (this.ExportDataOnly.value == '1');
			value.MergeMapsOnExport = this.MergeMapsOnExport.value;
			value.UseTemplates = this.UseTemplates.value;
			value.ExportMaps = this.ExportMaps.value;

			let selectedSensors: string[] = this.SensorTypes.value;
			value.AddPingMonitors = this.contains(selectedSensors, "ping");
			value.AddSnmpCpuMonitors = this.contains(selectedSensors, "snmp-cpu");
			value.AddSnmpIfMonitors = this.contains(selectedSensors, "snmp-if");
			value.AddSnmpIfMonitors_AllUp = this.contains(selectedSensors, "snmp-if-up");
			value.AddSnmpIfMonitors_AllPhysical = this.contains(selectedSensors, "snmp-if-physical");
			value.AddSnmpIfMonitors_AllUplinks = this.contains(selectedSensors, "snmp-if-uplink");
			value.AddSnmpMemoryMonitors = this.contains(selectedSensors, "snmp-memory");
			value.AddSnmpDiskFreeMonitors = this.contains(selectedSensors, "snmp-disk-free");
			value.AddSnmpUpTimeMonitors = this.contains(selectedSensors, "snmp-up-time");
			value.AddWmiCpuMonitors = this.contains(selectedSensors, "wmi-cpu");
			value.AddWmiMemoryMonitors = this.contains(selectedSensors, "wmi-memory");
			value.AddWmiUpTimeMonitors = this.contains(selectedSensors, "wmi-up-time");
			value.AddWmiDiskSpaceMonitors = this.contains(selectedSensors, "wmi-disk-space");

			value.DeviceCategories.Clear();
			value.DeviceGroups.Clear();
			this._MapData.forEach((mapData: any) => {
				if (mapData.Selected) {
					if (mapData.Owner == "System") {
						value.DeviceCategories.Add(mapData.Guid);
					}
					else {
						value.DeviceGroups.Add(mapData.Guid);
					}
				}
			});
				
			this.BuildForm();
		}
	}

	public get invalidTempChanges(): string[] {
		return ["PRTG Export"];
	}

	private validPrtgSettings(enableExport: UntypedFormControl, serverSettings: UntypedFormControl): ValidatorFn {
		return () => {
			if (enableExport.value) {
				if (!serverSettings.value) {
					return {
						validPrtgSettings: { valid: false }
					};
				}
				else {
					return null;
				}
			}
		}
	}

	private validGroupName(enableExport: UntypedFormControl, groupName: UntypedFormControl): ValidatorFn {
		return () => {
			if (enableExport.value) {
				if (!groupName.value) {
					return {
						validGroupName: { valid: false }
					};
				}
				else {
					return null;
				}
			}
		}
	}

	public ExportNow(): void {
		if (this.tempChanges) {
			this.messageService.displayInfoMessage(
				"You have unsaved changes to your PRTG export settings.\nPlease save before exporting.", 10000);
		}
		else {
			if (!!this.Network && !!this.Network.Guid) {
				let sessionGuid = this.identityService.sessionGuid;
				let netViewGuid = this.Network.Guid;
				let agentGuid = this.navService.location.agentGuid;
				let schedDiscGuid = this.navService.location.schedDiscGuid;
				this.discResultsService.runPrtgExport(sessionGuid, netViewGuid, agentGuid, schedDiscGuid).subscribe(
					(result: boolean) => {
						this.messageService.displayInfoMessage("PRTG Export has been initiated");
					},
					(error) => {
						this.messageService.displayErrorMessage("PRTG Export failed");
					});
			}
		}
	}

	public EditPrtgSettings(): void {

		// TODO
	}

	// ── PRTG group picker (Browse... buttons) ─────────────────────────────

	// True iff we have both a network (for netViewGuid) and a selected PRTG
	// server. The Browse... buttons can't function without both.
	public get canBrowsePrtgGroups(): boolean {
		return !!this.Network && !!this.Network.Guid && !!this.ServerSettings && !!this.ServerSettings.value;
	}

	public get pickerNetViewGuid(): string {
		return (this.Network && this.Network.Guid) ? this.Network.Guid : "";
	}

	public get pickerPrtgSettingsGuid(): string {
		return (this.ServerSettings && this.ServerSettings.value) ? this.ServerSettings.value : "";
	}

	public browseParentGroup(): void {
		if (!this.canBrowsePrtgGroups) return;
		this.parentPickerVisible = true;
	}

	public browseGroupName(): void {
		if (!this.canBrowsePrtgGroups) return;
		this.groupPickerVisible = true;
	}

	public onParentGroupPicked(event: { id: number, name: string }): void {
		this.ParentGroupName.setValue(event.name);
		this.ParentGroupName.markAsDirty();
	}

	public onGroupNamePicked(event: { id: number, name: string }): void {
		this.GroupName.setValue(event.name);
		this.GroupName.markAsDirty();
	}

	public doCustomSort(event): void {
		let comparer = function (a: any, b: any): number {
			let result = 0;

			switch (event.field) {
				default:
					// Compare string fields
					result = a[event.field].localeCompare(b[event.field]);
					break;
			}

			return result * event.order;
		}

		this._MapData.sort(comparer);
	}

	public testPrtgConnect(): void {

		let popUpMsg = "";

		//let prtgSettingsGuid = this.PrtgExport.ServerSettings.ToString();
		let prtgSettingsGuid = this.ServerSettings.value;
		if (prtgSettingsGuid) {
			let prtgSettings = this.FindSettingsByGuid(prtgSettingsGuid);
			if (prtgSettings) {
				this.toolsService.testPrtgConnection(prtgSettings.ServerUrl, prtgSettings.UserName, prtgSettings.Passhash, prtgSettings.ServiceApiKey).subscribe(
					(result: PrtgConnectResultDto) => {
						if (result.succeeded) {
							if (result.uvx_service_succeeded) {
								popUpMsg = "PRTG Connection SUCCEEDED.  PRTG Version [" + result.prtg_version + "]";
								this.messageService.displayInfoMessage(popUpMsg);
							}
							else {
								popUpMsg = "PRTG Connection SUCCEEDED.  PRTG Version [" + result.prtg_version + "] ";
								popUpMsg += result.message
								this.messageService.displayErrorMessage(popUpMsg);
							}
						}
						else {
							popUpMsg = result.message;
							this.messageService.displayErrorMessage(popUpMsg);
						}
					},
					(error) => {
						this.messageService.displayErrorMessage("PRTG connection FAILED");
					});
			}
			else {
				this.messageService.displayErrorMessage("INVALID PRTG settings");
			}
		}
	}
}
